/* eslint-disable max-len */
import {makeObservable, observable, action, computed} from 'mobx';

/**
 * 页面类型
 * 替代原来的 6 个 getters (isInitialingPage, isRunningPage, etc.)
 */
export type PageType = 'SETUP' | 'RUNNING' | 'REPORT';
/**
 * 判断是否为运行阶段的页面
 */
const RUNNING_PAGES: JobRunningPageType[] = ['COLDSTART', 'EVOLVE', 'REPORT'];

/**
 * 判断是否为初始化阶段的页面
 */
const SETUP_PAGES: string[] = ['CREATED', 'INITIALING'];

/**
 * 任务执行状态管理
 *
 * 职责:
 * - 管理任务的生命周期状态 (CREATED → RUNNING → COMPLETED)
 * - 提供清晰的状态判断方法
 * - 替代原 TaskRunningStore 中的状态相关逻辑
 *
 * 设计原则:
 * - 单一职责:只管理任务执行状态,不管理数据/UI/加载状态
 * - 状态机:用枚举和映射替代字符串判断
 * - 不可变性:状态转换通过 @action 方法,不允许直接修改
 */
export class TaskExecutionStore {
    /** 任务 ID */
    job_id: string = '';

    /** 任务状态 (用枚举,不用字符串) */
    state: TaskState | '' = '';

    /** 当前阶段 */
    stage: STAGE_TYPE | '' = '';

    /** 任务对应的阶段页面（后端返回） */
    jobRunningPage: JobRunningPageType | '' = '';

    /** 任务进度 (0-100) */
    percentage: number = 0;

    /** 错误信息 */
    errorMessage: string = '';

    constructor(jobId: string = '') {
        this.job_id = jobId;
        makeObservable(this, {
            job_id: observable,
            state: observable,
            stage: observable,
            jobRunningPage: observable,
            percentage: observable,
            errorMessage: observable,
            isRunningPage: computed,
            isInitialingPage: computed,
            canPause: computed,
            canResume: computed,
            canTerminate: computed,
            isRunning: computed,
            isFinished: computed,
            isFailed: computed,
            isCancelled: computed,
            isPaused: computed,
            isCompleted: computed,
            isReportCompleted: computed,
            isColdCompleted: computed,
            isDisabledConfigTask: computed,
            isEvolveCompleted: computed,
            shouldShowReportModal: computed,
            shouldAutoEnterReport: computed,
            setState: action.bound,
            setProgress: action.bound,
            setError: action.bound,
            setJobId: action.bound,
            setJobRunningPage: action.bound,
            pause: action.bound,
            resume: action.bound,
            cancel: action.bound,
            complete: action.bound,
            reset: action.bound,
            setStateFromLegacy: action.bound
        });
    }
    /**
     * 是否为初始化页面（可行性判断阶段）
     * 对应原 TaskRunningStore.isInitialingPage
     *
     * 逻辑:
     * 1. 如果当前页面是运行中页面类型(INITIALING/CREATED)，是初始化页面
     * 2. 如果页面类型为 INITIALING/CREATED，或者 page不存在 状态为 INITIALING/CREATED 则是初始化页面
     */
    get isInitialingPage(): boolean {
        // page类型为初始化中/已创建，则认为是初始化页面
        if (SETUP_PAGES.includes(this.jobRunningPage) && ['CREATED', 'INITIALING', 'FAILED', 'COMPLETED', 'CANCELLED'].includes(this.state as TaskState)) {
            return true;
        }
        // 如果 page 不存在且状态为初始化的状态
        if (!this.jobRunningPage && ['DRAFT', 'CREATED', 'INITIALING', 'FAILED', 'COMPLETED', 'CANCELLED'].includes(this.state as TaskState)) {
            return true;
        }

        return false;
    }

    /**
     * 是否为运行中页面
     * 对应原 TaskRunningStore.isRunningPage
     */
    get isRunningPage(): boolean {
        return RUNNING_PAGES.includes(this.jobRunningPage as JobRunningPageType);
    }

    /** 是否可以暂停 */
    get canPause(): boolean {
        return this.state === TaskState.RUNNING
               && (this.stage === 'COLDSTART' || this.stage === 'EVOLVE');
    }

    /** 是否可以继续 */
    get canResume(): boolean {
        return this.state === TaskState.PAUSED;
    }

    /**
     * 是否可以手动终止任务
     * 对应原 TaskRunningStore.isTerminated
     *
     * 逻辑:
     * 1. 任务所有阶段，任务失败和已经取消，就不再允许终止操作
     * 2. RESULT阶段：非任务失败、取消、完成阶段都可以终止
     * 4. 其他情况默认可以终止
     */
    get canTerminate(): boolean {
        // 任务失败和已经终止和已经是报告阶段，就不再允许任务任何状态操作
        if (this.isFailed || this.isCancelled || this.isTerminated || this.stage === TaskStage.REPORT) {
            return false;
        }

        // EVOLVE阶段，非任务失败、取消、完成阶段都可以终止
        if (this.stage === 'EVOLVE') {
            return !(this.state === TaskState.COMPLETED);
        }

        // 其他情况默认可以终止
        return true;
    }

    /** 是否正在运行 */
    get isRunning(): boolean {
        return this.state === TaskState.RUNNING;
    }

    /** 是否已结束 */
    get isFinished(): boolean {
        return this.state === TaskState.COMPLETED
            || this.state === TaskState.FAILED
            || this.state === TaskState.CANCELLED
            || this.state === TaskState.TERMINATED;
    }

    /** 是否失败 */
    get isFailed(): boolean {
        return this.state === TaskState.FAILED;
    }

    /** 是否已取消 */
    get isCancelled(): boolean {
        return this.state === TaskState.CANCELLED;
    }

    /** 是否已终止 */
    get isTerminated(): boolean {
        return this.state === TaskState.TERMINATED;
    }

    /**
     * 任务是否已暂停
     * 对应原 TaskRunningStore.isPause
     */
    get isPaused(): boolean {
        return this.state === TaskState.PAUSED;
    }

    /**
     * 整个任务是否正常迭代结束
     * 对应原 TaskRunningStore.isCompleted
     */
    get isCompleted(): boolean {
        return this.state === TaskState.COMPLETED && this.stage === TaskStage.REPORT && this.jobRunningPage === TaskStage.REPORT;
    }

    get isReportCompleted(): boolean {
        return this.state === TaskState.COMPLETED && this.stage === TaskStage.REPORT;
    }
    /**
     * 判断任务 stage1（冷启动阶段）是否完成
     */
    get isColdCompleted(): boolean {
        return this.stage === 'COLDSTART' && this.state === TaskState.COMPLETED;
    }
    /**
     * 判断任务 stage2（演化阶段）是否完成
     */
    get isEvolveCompleted(): boolean {
        return this.stage === TaskStage.EVOLVE && this.state === TaskState.COMPLETED;
    }

    /**
     * 任务重新配置按钮 只有在演化阶段任务 运行和暂停状态可点击 'RUNNING', 'PAUSED', 'CREATED', 'INITIALING'
     * */
    get isDisabledConfigTask(): boolean {
        if (['RUNNING', 'PAUSED', 'CREATED', 'INITIALING'].includes(this.state) && this.stage === 'EVOLVE') {
            return false;
        }

        return true;
    }


    /**
     * 是否应该显示报告阶段的 BottomLeftModal 弹窗
     * 判断条件: status === COMPLETED && stage === REPORT && page === REPORT
     */
    get shouldShowReportModal(): boolean {
        return this.state === TaskState.COMPLETED
            && this.stage === TaskStage.REPORT;
        // && this.jobRunningPage === TaskStage.REPORT;
    }

    /**
     * 演化阶段是否已完成且需要自动进入报告阶段
     * 判断条件: stage === EVOLVE && status === COMPLETED && page === EVOLVE
     */
    get shouldAutoEnterReport(): boolean {
        return this.stage === TaskStage.EVOLVE
            && this.state === TaskState.COMPLETED
            && this.jobRunningPage === TaskStage.EVOLVE;
    }

    /**
     * 设置任务 ID
     */
    setJobId(jobId: string) {
        this.job_id = jobId;
    }

    /**
     * 设置任务状态
     */
    setState(state: TaskState) {
        this.state = state;
    }

    /**
     * 设置任务运行页面（后端返回）
     */
    setJobRunningPage(page: JobRunningPageType | '') {
        this.jobRunningPage = page;
    }

    /**
     * 设置阶段和进度
     * 注意: 仅设置阶段和进度，不自动更新状态
     * 状态应该通过 setState 或其他状态方法单独设置
     */
    setProgress(stage: STAGE_TYPE, percentage: number) {
        this.stage = stage;
        this.percentage = percentage;
    }

    /**
     * 设置错误信息
     */
    setError(message: string) {
        this.errorMessage = message;
        if (message) {
            this.state = TaskState.FAILED;
        }
    }

    /**
     * 暂停任务
     */
    pause() {
        if (this.canPause) {
            this.state = TaskState.PAUSED;
        }
    }

    /**
     * 恢复任务
     */
    resume() {
        if (this.canResume) {
            // 恢复到运行状态
            this.state = TaskState.RUNNING;
        }
    }

    /**
     * 取消任务
     */
    cancel() {
        this.state = TaskState.CANCELLED;
    }

    /**
     * 完成任务
     */
    complete() {
        this.state = TaskState.COMPLETED;
    }

    /**
     * 重置 Store
     */
    reset() {
        this.job_id = '';
        this.state = '';
        this.stage = '';
        this.jobRunningPage = '';
        this.percentage = 0;
        this.errorMessage = '';
    }

    /**
     * 从旧的字符串状态迁移到新的枚举状态
     * 兼容层:在迁移期间使用
     *
     * 注意: COLDSTART/EVOLVE/REPORT 是阶段(stage)，不是状态(state)
     * 这里为了兼容旧代码，当遇到阶段字符串时，将其映射为 RUNNING 状态
     */
    setStateFromLegacy(legacyStatus: string) {
        const statusMap: Record<string, TaskState> = {
            'CREATED': TaskState.CREATED,
            'INITIALING': TaskState.INITIALING,
            'COLDSTART': TaskState.RUNNING, // 阶段，映射为运行状态
            'EVOLVE': TaskState.RUNNING, // 阶段，映射为运行状态
            'REPORT': TaskState.COMPLETED, // 阶段，映射为完成状态
            'PAUSED': TaskState.PAUSED,
            'FAILED': TaskState.FAILED,
            'CANCELLED': TaskState.CANCELLED,
            'TERMINATED': TaskState.TERMINATED,
            'RUNNING': TaskState.RUNNING,
            'COMPLETED': TaskState.COMPLETED,
            'DRAFT': TaskState.DRAFT
        };

        this.state = statusMap[legacyStatus] || TaskState.CREATED;
    }
}
